import React, { useEffect, useState, useRef } from "react";
import { Timer } from "lucide-react";

function getTimerTone(percentLeft) {
  if (percentLeft <= 30) return "red";
  if (percentLeft <= 60) return "yellow";
  return "green";
}

const TIMER_BAR_COLORS = {
  green: "#00a379",
  yellow: "#ffc233",
  red: "#e84a4a",
};

export default function OrderTimer({ order, compact }) {
  // Function to get total time based on platform
  const getTotalTime = () => {
    const platform = order?.platform?.toLowerCase();

    // Jahez = 5 minutes
    if (platform === "jahez") return 5 * 60;

    // Keeta = 5 minutes (already implemented)
    if (platform === "keeta") return 5 * 60;

    // The Chefz = 10 minutes
    if (platform === "the chefz" || platform === "chefz") return 10 * 60;

    // Ninja = 30 minutes
    if (platform === "ninja") return 30 * 60;

    // Chat Agent / Digital Catalog = 15 minutes
    if (
      platform === "chat_agent" ||
      platform === "chat agent" ||
      platform === "digital_catalog" ||
      platform === "digital catalog"
    ) {
      return 15 * 60;
    }

    // Default fallback
    return 10 * 60;
  };

  const totalTime = getTotalTime();

  // Calculate time left
  const calculateTimeLeft = () => {
    if (!order?.createdAt) return totalTime;
    const createdAt = new Date(order.createdAt);
    const now = new Date();
    const elapsedSeconds = Math.floor((now - createdAt) / 1000);
    return Math.max(totalTime - elapsedSeconds, 0);
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
  const timerRef = useRef(null);

  useEffect(() => {
    if (timeLeft <= 0) return;

    timerRef.current = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      if (newTimeLeft <= 0) {
        setTimeLeft(0);
        clearInterval(timerRef.current);
      } else {
        setTimeLeft(newTimeLeft);
      }
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [order?.createdAt, totalTime]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const progress = ((totalTime - timeLeft) / totalTime) * 100;
  const percentLeft = (timeLeft / totalTime) * 100;
  const tone = getTimerTone(percentLeft);
  const progressColor = TIMER_BAR_COLORS[tone];

  // Extra small circle settings
  const size = 36; // very small diameter
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div
      className={`oh-order-timer-wrap${compact ? " oh-order-timer-wrap--compact" : ""}`}
      style={{
        maxWidth: compact ? "100%" : "fit-content",
        fontFamily: "sans-serif",
      }}
    >
      {compact ? (
        <>
          <div className={`oh-order-timer oh-order-timer--${tone}`}>
            <span className="oh-order-timer__group">
              <span className="oh-order-timer__icon" aria-hidden="true">
                <Timer size={10} strokeWidth={2.25} />
              </span>
              <span className="oh-order-timer__time">{formatTime(timeLeft)}</span>
            </span>
          </div>
          <div className="oh-order-timer__bar">
            <div
              className={`oh-order-timer__bar-fill oh-order-timer__bar-fill--${tone}`}
              style={{
                width: `${progress}%`,
                background: progressColor,
              }}
            />
          </div>
        </>
      ) : (
        // Non-compact: Extra small circular progress bar
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "2px",
          }}
        >
          <svg width={size} height={size}>
            {/* Background circle */}
            <circle
              stroke="#e0e0e0"
              fill="transparent"
              strokeWidth={strokeWidth}
              r={radius}
              cx={size / 2}
              cy={size / 2}
            />
            {/* Progress circle */}
            <circle
              stroke={progressColor}
              fill="transparent"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              r={radius}
              cx={size / 2}
              cy={size / 2}
              style={{
                transition: "stroke-dashoffset 1s linear, stroke 0.3s ease",
              }}
            />
            {/* Time text inside */}
            <text
              x="50%"
              y="50%"
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="10"
              fontWeight="bold"
            >
              {formatTime(timeLeft)}
            </text>
          </svg>
        </div>
      )}
    </div>
  );
}
